import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, CardHeader, Spinner } from "reactstrap";
import TableContainerNoPagination from "../../../Components/Common/TableContainerNoPagination";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSolicitudes } from "../../../slices/thunks";
import { createSelector } from "reselect";

// Componente para mostrar solicitudes relacionadas del mismo solicitante
const SolicitudesRelacionadas = ({ solicitud }) => {
  const [error, setError] = useState(null);
  const [currentSolicitudId, setCurrentSolicitudId] = useState(null);
  
  const dispatch = useDispatch();
  
  // Definir el selector para obtener las solicitudes del estado de Redux
  const selectLayoutState = (state) => state;
  
  const pageData = createSelector(selectLayoutState, (state) => ({
    solicitudes: state.Solicitudes.solicitudes,
    loading: state.Solicitudes.loading,
  }));
  
  // Obtener las solicitudes directamente del estado de Redux
  const { solicitudes, loading } = useSelector(pageData);

  // Guardar el ID de la solicitud actual para filtrado
  useEffect(() => {
    if (solicitud && solicitud.id) {
      setCurrentSolicitudId(solicitud.id);
    }
  }, [solicitud]);
  
  // Obtener solicitudes relacionadas cuando el componente se monta
  useEffect(() => {
    // Solo buscar si tenemos una cédula de solicitante
    if (!solicitud || !solicitud.solicitanteCedula) {
      return;
    }
    
    // Construir query con parámetros para buscar solicitudes relacionadas
    const queryParams = [];
    
    // Añadir cédula del solicitante como término de búsqueda
    queryParams.push(`query=${encodeURIComponent(solicitud.solicitanteCedula)}`);
    
    // Filtrar por estatus "Nueva Solicitud"
    queryParams.push(`estatus=Nueva%20Solicitud`);
    
    // Añadir código de proveedor si está disponible
    if (solicitud.codigoProveedor) {
      queryParams.push(`codProveedor=${encodeURIComponent(solicitud.codigoProveedor)}`);
    }
    
    const finalQuery = queryParams.join("&");
    
    // Usar el método getSolicitudes con dispatch
    dispatch(getSolicitudes(finalQuery))
      .catch(err => {
        setError("No se pudieron cargar las solicitudes relacionadas");
      });
  }, [dispatch, solicitud]);

  // Definir las columnas para la tabla de solicitudes
  const columns = useMemo(
    () => [
      {
        header: "ID",
        cell: (cell) => {
          return <span className="fw-semibold">{cell.getValue()}</span>;
        },
        accessorKey: "identificador",
        enableColumnFilter: false,
      },
      {
        header: "Tipo",
        accessorKey: "tipo",
        enableColumnFilter: false,
        cell: (cell) => {
          const tipo = cell.getValue();
          if (!tipo) return <span>-</span>;
          
          let badgeClass = "";
          let colorClass = "";
      
          switch (tipo) {
            case "Aguda":
              badgeClass = "Aguda";
              colorClass = "bg-danger-subtle text-danger";
              break;
            case "Cronica":
              badgeClass = "Crónica";
              colorClass = "bg-success-subtle text-success";
              break;
            default:
              colorClass = "bg-info-subtle text-info";
          }
      
          return (
            <span className={`badge ${colorClass}`}>
              {badgeClass || tipo}
            </span>
          );
        },
      },
      {
        header: "Fecha / Hora",
        accessorKey: "creado",
        enableColumnFilter: false,
        cell: (cell) => {
          const fecha = new Date(cell.getValue());
          return fecha.toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
      {
        header: "Cod. Farmacia",
        accessorKey: "codigoProveedor",
        enableColumnFilter: false,
        cell: (cell) => {
          const codigoProveedor = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!codigoProveedor) {
            return <span>-</span>;
          }
          
          let badgeClass = "";
          let colorClass = "";
      
          switch (codigoProveedor) {
            case "00077":
              badgeClass = "Caracas";
              colorClass = "bg-primary-subtle text-primary";
              break;
            case "00082":
              badgeClass = "Mérida";
              colorClass = "bg-danger-subtle text-danger";
              break;
            case "00083":
              badgeClass = "Carabobo";
              colorClass = "bg-warning-subtle text-info";
              break;
            default:
              badgeClass = "Lara";
              colorClass = "bg-warning-subtle text-warning";
          }
      
          return (
            <span className={`badge ${colorClass}`}>
              {codigoProveedor} - {badgeClass}
            </span>
          );
        },
      },
      {
        header: "Estado",
        accessorKey: "direccion",
        enableColumnFilter: false,
        cell: (cell) => {
          const direccion = cell.getValue();
          if (!direccion) {
            return <span>-</span>;
          }
          
          const estadoMatch = direccion.match(/Estado\s+([^,]+)(?:,|$)/);
          if (estadoMatch && estadoMatch[1]) {
            const estadoTitulo = estadoMatch[1].trim().toLowerCase().split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            return <span>{estadoTitulo}</span>;
          } else {
            return <span> - </span>; 
          }
        },
      },
      {
        header: "Prioridad",
        accessorKey: "prioridad",
        enableColumnFilter: false,
        cell: (cell) => {
          const prioridad = cell.getValue();
          if (!prioridad) {
            return <span>-</span>;
          }
          let badgeClass = "";

          switch (prioridad.toLowerCase()) {
            case "alta":
              badgeClass = "danger";
              break;
            case "media":
              badgeClass = "warning";
              break;
            case "baja":
              badgeClass = "success";
              break;
            default:
              badgeClass = "info";
          }

          return (
            <span
              className={`badge bg-${badgeClass}-subtle text-${badgeClass}`}
            >
              {prioridad}
            </span>
          );
        },
      },
      {
        header: "Estatus",
        accessorKey: "estatus",
        enableColumnFilter: false,
        cell: (cell) => {
          const estatus = cell.getValue();
          if (!estatus) {
            return <span>-</span>;
          }
          let badgeClass = "";

          switch (estatus.toLowerCase()) {
            case "entregado":
              badgeClass = "success";
              break;
            case "pendiente":
              badgeClass = "warning";
              break;
            case "cancelado":
              badgeClass = "danger";
              break;
            default:
              badgeClass = "info";
          }

          return (
            <span
              className={`badge bg-${badgeClass}-subtle text-${badgeClass}`}
            >
              {estatus}
            </span>
          );
        },
      },
      {
        header: "Acción",
        accessorKey: "accion",
        enableColumnFilter: false,
        cell: (cell) => {
          const rowId = cell.row.original.id;
          return (
            <div className="d-flex gap-3">
              <Link
                to={`/solicitud/ver/${rowId}`}
                className="text-primary d-inline-block"
              >
                <i className="ri-eye-fill fs-16"></i>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  // Procesar las solicitudes del estado global para filtrar la solicitud actual
  const solicitudesRelacionadas = useMemo(() => {
    if (!solicitudes || !solicitudes[0] || !currentSolicitudId) {
      return [];
    }
    
    // Filtrar las solicitudes para excluir la solicitud actual
    return solicitudes[0].filter(item => item.id !== currentSolicitudId);
  }, [solicitudes, currentSolicitudId]);
  
  // Si está cargando o no hay solicitudes relacionadas, no mostrar el componente
  if ((!loading && Array.isArray(solicitudesRelacionadas) && solicitudesRelacionadas.length === 0)) {
    return null;
  }

  return (
    <Card className="mb-0">
      <CardHeader className="align-items-center d-flex">
        <h5 className="card-title mb-0 flex-grow-1">
          <i className="ri-file-list-3-line align-middle me-1 text-muted"></i>
          Solicitudes Relacionadas
        </h5>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-center">
            <Spinner color="primary">Cargando...</Spinner>
          </div>
        ) : error ? (
          <div className="alert alert-danger mb-0">{error}</div>
        ) : (
          <TableContainerNoPagination
            columns={columns}
            data={solicitudesRelacionadas}
            customPageSize={5}
            divClass="table-responsive"
            className="custom-header-css"
            isLoading={loading}
          />
        )}
      </CardBody>
    </Card>
  );
};

// Optimización: React.memo para evitar re-renders innecesarios
export default React.memo(SolicitudesRelacionadas, (prevProps, nextProps) => {
  return prevProps.solicitud?.id === nextProps.solicitud?.id;
});
